import { useEffect, useState } from 'react';
import { getCreditosOverview, getCreditosOrg } from '@/lib/api';

// ── tipos (espelham admin-api/routers/creditos.py) ───────────────────────────

interface Totais {
  compras: number;
  consumo: number;
  grants: number;
  estornos: number;
  n_compras: number;
  n_consumo: number;
  n_grants: number;
  n_estornos: number;
}

interface SemanaSerie {
  semana: string;
  compras: number;
  consumo: number;
  grants: number;
  estornos: number;
}

interface TopOrg {
  org_id: number;
  name: string;
  plan_type: string;
  saldo: number;
  calculos: number;
  consumidos: number;
}

interface OrgParada {
  org_id: number;
  name: string;
  plan_type: string;
  saldo: number;
  ultimo_consumo: string | null;
  comprados: number;
}

interface OverviewData {
  disponivel: boolean;
  erro?: string;
  totais: Totais;
  serie_semanal: SemanaSerie[];
  top_consumo_30d: TopOrg[];
  saldo_parado: OrgParada[];
  params: { saldo_parado_min: number; janela_parado_dias: number };
}

interface LedgerRow {
  id: number;
  type: string;
  tipo_label: string;
  amount: number;
  balance_after: number;
  description: string | null;
  processo_id: number | null;
  package_key: string | null;
  created_at: string | null;
}

interface OrgDetail {
  disponivel: boolean;
  erro?: string;
  org: {
    id: number;
    name: string;
    slug: string;
    plan_type: string;
    saldo: number;
    created_at: string | null;
  };
  cards: {
    comprados: number;
    consumidos: number;
    cortesia: number;
    estornados: number;
    n_transacoes: number;
  };
  page: number;
  page_size: number;
  total: number;
  ledger: LedgerRow[];
}

const fmtData = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('pt-BR') : '—';

const BADGE: Record<string, string> = {
  Compra: 'bg-emerald-50 text-emerald-600',
  'Boas-vindas': 'bg-teal-50 text-teal-600',
  Indicação: 'bg-teal-50 text-teal-600',
  Cortesia: 'bg-teal-50 text-teal-600',
  Estorno: 'bg-amber-50 text-amber-600',
  'Cálculo executado': 'bg-slate-100 text-slate-500',
  Ajuste: 'bg-slate-100 text-slate-500',
};

function Card({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-white">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-3xl font-bold ${accent ?? 'text-slate-800'}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function CreditosPage() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [orgPage, setOrgPage] = useState(1);
  const [orgLoading, setOrgLoading] = useState(false);

  useEffect(() => {
    getCreditosOverview()
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function abrirOrg(orgId: number, page = 1) {
    setOrgLoading(true);
    try {
      const r = await getCreditosOrg(orgId, page);
      if (r.data?.disponivel) {
        setOrg(r.data);
        setOrgPage(page);
      }
    } catch {
      /* silencioso — padrão das outras abas */
    } finally {
      setOrgLoading(false);
    }
  }

  if (loading && !data)
    return <div className="p-6 text-slate-400">Carregando…</div>;
  if (!data) return <div className="p-6 text-slate-400">Sem dados.</div>;
  if (!data.disponivel)
    return (
      <div className="p-6 text-rose-500">
        Indisponível: {data.erro} (grants de
        admin-api/migrations/003_creditos_grants.sql aplicados?)
      </div>
    );

  const t = data.totais;
  const serie = data.serie_semanal;
  const maxSemana = Math.max(
    1,
    ...serie.map((s) => Math.max(s.compras, s.consumo, s.grants)),
  );
  const totalPagesOrg = org ? Math.max(1, Math.ceil(org.total / org.page_size)) : 1;

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">
          Créditos &amp; Receita
        </h1>
        <p className="text-sm text-slate-500">
          Economia de créditos da plataforma. Receita real = SÓ compras via
          Stripe (type=purchase); grant manual/boas-vindas/indicação
          (adjustment) NUNCA é receita.
        </p>
      </div>

      {/* KPIs topo — totais acumulados por type */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card
          label="Créditos comprados (Stripe)"
          value={String(t.compras)}
          accent="text-emerald-600"
          sub={`${t.n_compras} compras — a receita real`}
        />
        <Card
          label="Créditos consumidos"
          value={String(t.consumo)}
          sub={`${t.n_consumo} cálculos cobrados`}
        />
        <Card
          label="Grants (cortesia)"
          value={String(t.grants)}
          accent="text-teal-600"
          sub={`${t.n_grants} boas-vindas + indicação + manual`}
        />
        <Card
          label="Estornos"
          value={String(t.estornos)}
          accent="text-amber-600"
          sub={`${t.n_estornos} falhas/correções devolvidas`}
        />
      </div>

      {/* Série semanal compras × consumo × grants */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-1">
          Compras × consumo × grants (12 semanas)
        </h2>
        <p className="text-xs text-slate-400 mb-3">
          Créditos por semana:{' '}
          <span className="text-emerald-600">■ compras</span> ·{' '}
          <span className="text-slate-500">■ consumo</span> ·{' '}
          <span className="text-teal-600">■ grants</span>
        </p>
        <div className="space-y-1.5">
          {serie.map((s) => (
            <div key={s.semana} className="flex items-center gap-2 text-xs">
              <span className="w-20 text-slate-500">{s.semana}</span>
              <div className="flex-1 space-y-0.5">
                <div className="bg-slate-100 rounded h-2.5 overflow-hidden">
                  <div
                    className="bg-emerald-400 h-2.5"
                    style={{ width: `${(100 * s.compras) / maxSemana}%` }}
                  />
                </div>
                <div className="bg-slate-100 rounded h-2.5 overflow-hidden">
                  <div
                    className="bg-slate-400 h-2.5"
                    style={{ width: `${(100 * s.consumo) / maxSemana}%` }}
                  />
                </div>
                <div className="bg-slate-100 rounded h-2.5 overflow-hidden">
                  <div
                    className="bg-teal-400 h-2.5"
                    style={{ width: `${(100 * s.grants) / maxSemana}%` }}
                  />
                </div>
              </div>
              <span className="w-14 text-right text-emerald-600 font-semibold">
                +{s.compras}
              </span>
              <span className="w-14 text-right text-slate-600">−{s.consumo}</span>
              <span className="w-12 text-right text-teal-600">+{s.grants}</span>
            </div>
          ))}
          {serie.length === 0 && (
            <div className="text-sm text-slate-400">Sem transações ainda.</div>
          )}
        </div>
      </div>

      {/* Top consumo 30d + saldo parado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
            Top-10 orgs por consumo (30d)
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2 text-left">Org</th>
                <th className="px-3 py-2 text-left">Plano</th>
                <th className="px-3 py-2 text-right">Cálculos</th>
                <th className="px-3 py-2 text-right">Consumidos</th>
                <th className="px-3 py-2 text-right">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {data.top_consumo_30d.map((o) => (
                <tr
                  key={o.org_id}
                  onClick={() => void abrirOrg(o.org_id)}
                  className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                  title="Ver extrato da org"
                >
                  <td className="px-4 py-2 text-slate-700">
                    {o.name}
                    <span className="text-xs text-slate-400"> #{o.org_id}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{o.plan_type}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{o.calculos}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-800">
                    {o.consumidos}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">{o.saldo}</td>
                </tr>
              ))}
              {data.top_consumo_30d.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-slate-400">
                    Nenhum consumo nos últimos 30 dias.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold text-slate-700">
            Saldo alto parado{' '}
            <span className="text-xs font-normal text-slate-400">
              (≥{data.params.saldo_parado_min} créditos, sem consumo há{' '}
              {data.params.janela_parado_dias}d — risco churn/refund)
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2 text-left">Org</th>
                <th className="px-3 py-2 text-left">Plano</th>
                <th className="px-3 py-2 text-right">Saldo</th>
                <th className="px-3 py-2 text-right">Comprados</th>
                <th className="px-3 py-2 text-left">Último consumo</th>
              </tr>
            </thead>
            <tbody>
              {data.saldo_parado.map((o) => (
                <tr
                  key={o.org_id}
                  onClick={() => void abrirOrg(o.org_id)}
                  className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                  title="Ver extrato da org"
                >
                  <td className="px-4 py-2 text-slate-700">
                    {o.name}
                    <span className="text-xs text-slate-400"> #{o.org_id}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{o.plan_type}</td>
                  <td className="px-3 py-2 text-right font-semibold text-amber-600">
                    {o.saldo}
                  </td>
                  <td
                    className={`px-3 py-2 text-right ${
                      o.comprados ? 'text-rose-600 font-semibold' : 'text-slate-400'
                    }`}
                  >
                    {o.comprados}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-400">
                    {fmtData(o.ultimo_consumo)}
                  </td>
                </tr>
              ))}
              {data.saldo_parado.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-slate-400">
                    Nenhuma org com saldo alto parado. 🎉
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drill-down da org (clique nas tabelas acima) */}
      {orgLoading && !org && (
        <div className="text-sm text-slate-400">Carregando extrato da org…</div>
      )}
      {org && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-700">
              Extrato — {org.org.name}{' '}
              <span className="text-xs font-normal text-slate-400">
                #{org.org.id} · {org.org.plan_type} · cliente desde{' '}
                {fmtData(org.org.created_at)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setOrg(null)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Fechar ✕
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4">
            <Card label="Saldo" value={String(org.org.saldo)} accent="text-teal-600" />
            <Card
              label="Comprados"
              value={String(org.cards.comprados)}
              accent="text-emerald-600"
            />
            <Card
              label="Cortesia"
              value={String(org.cards.cortesia)}
              accent="text-teal-600"
            />
            <Card label="Consumidos" value={String(org.cards.consumidos)} />
            <Card
              label="Estornados"
              value={String(org.cards.estornados)}
              accent="text-amber-600"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left">Data</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-right">Créditos</th>
                  <th className="px-3 py-2 text-left">Descrição</th>
                  <th className="px-3 py-2 text-right">Caso</th>
                  <th className="px-3 py-2 text-right">Saldo após</th>
                </tr>
              </thead>
              <tbody className={orgLoading ? 'opacity-50' : ''}>
                {org.ledger.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">
                      {fmtData(l.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`text-xs rounded-full px-2 py-0.5 whitespace-nowrap ${
                          BADGE[l.tipo_label] ?? 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {l.tipo_label}
                      </span>
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-semibold ${
                        l.amount > 0 ? 'text-teal-600' : 'text-slate-600'
                      }`}
                    >
                      {l.amount > 0 ? `+${l.amount}` : l.amount}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[380px] truncate" title={l.description ?? ''}>
                      {l.description ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-500">
                      {l.processo_id != null ? `#${l.processo_id}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600">
                      {l.balance_after}
                    </td>
                  </tr>
                ))}
                {org.ledger.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-4 text-center text-slate-400">
                      Nenhuma transação.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPagesOrg > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                {org.total} transações · página {org.page} de {totalPagesOrg}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={org.page <= 1 || orgLoading}
                  onClick={() => void abrirOrg(org.org.id, orgPage - 1)}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  disabled={org.page >= totalPagesOrg || orgLoading}
                  onClick={() => void abrirOrg(org.org.id, orgPage + 1)}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                >
                  Próxima →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
